import React, { useState, useRef, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import { getTermsDocumentUrl } from '@/use-cases/get-terms-document-url';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import styles from './scrollable-agreement.module.scss';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2Icon } from 'lucide-react';
import notifyHB from '@/config/notifyHB';
import { CheckboxLabel } from '@/components/checkbox-label/checkbox-label';
import { getErrorReason, trackEvent } from '@/utils/analytics';

interface ScrollableAgreementProps {
	nextStep: () => void;
}

const ScrollableAgreement: React.FC<ScrollableAgreementProps> = ({
	nextStep,
}) => {
	const [numPages, setNumPages] = useState<number>(0);
	const [isBottom, setIsBottom] = useState(false);
	const [pageWidth, setPageWidth] = useState(0);
	const [documentUrl, setDocumentUrl] = useState<File | null>(null);
	const [agreementAccepted, setAgreementAccepted] = useState(false);
	const pdfRef = useRef<HTMLDivElement>(null);

	const checkScroll = () => {
		const element = pdfRef.current;
		if (element) {
			const { scrollTop, scrollHeight, clientHeight } = element;
			if (scrollTop + clientHeight >= scrollHeight - 10) {
				setIsBottom(true);
			}
		}
	};

	useEffect(() => {
		getTermsDocumentUrl()
			.then((documentUrl) => {
				setDocumentUrl(documentUrl);
			})
			.catch((error) => {
				trackEvent('onboarding_error', {
					step: 'terms',
					reason: `terms_pdf_${getErrorReason(error)}`,
				});
				notifyHB(
					'scrollable-agreement',
					'ScrollableAgreement',
					`Fail to get agreement document PDF with ${error}`,
				);
			});
	}, []);

	useEffect(() => {
		const element = pdfRef.current;
		if (element && documentUrl) {
			element.addEventListener('scroll', checkScroll);
			setPageWidth(element.clientWidth - 40); // Subtract padding
			return () => element.removeEventListener('scroll', checkScroll);
		}
	}, [documentUrl]);

	function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
		setNumPages(numPages);
	}

	return (
		<div className={styles['scrollable-agreement']}>
			<h3>Contrato de solicitante/suscriptor</h3>
			<Alert variant="default" className={styles['alert']}>
				<CheckCircle2Icon />
				<AlertTitle shouldEllipsisizeText={false}>
					Debe leer todo el documento para poder continuar
				</AlertTitle>
			</Alert>
			<div
				ref={pdfRef}
				style={{
					height: '500px',
					overflowY: 'scroll',
					border: '1px solid #ccc',
					padding: 2,
					marginBottom: 2,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}
			>
				{documentUrl && (
					<Document
						file="/pdfs/agreement.pdf" //{documentUrl} // Make sure this path is correct
						onLoadSuccess={onDocumentLoadSuccess}
					>
						{Array.from(new Array(numPages), (el, index) => (
							<Page
								key={`page_${index + 1}`}
								pageNumber={index + 1}
								width={pageWidth}
								renderTextLayer={false}
								renderAnnotationLayer={false}
							/>
						))}
					</Document>
				)}
			</div>
			<CheckboxLabel
				label={'Acepto los términos y condiciones'}
				checked={agreementAccepted}
				onCheckedChange={setAgreementAccepted}
				disabled={!isBottom}
			/>
			<Button
				variant="default"
				onClick={nextStep}
				disabled={!agreementAccepted}
				style={{ width: 'min-content', alignSelf: 'end' }}
			>
				Continuar
			</Button>
		</div>
	);
};

export default ScrollableAgreement;
